import React, { useState } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, AppBar, Toolbar, Typography, Button,
  IconButton, Drawer, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Divider, CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assignment as ProjectsIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  ContentCopy as ExtractIcon,
} from '@mui/icons-material';
import Projects from './components/projects/Projects';
import Dashboard from './components/dashboard/Dashboard';
import Settings from './components/settings/Settings';
import Extract from './components/extract/Extract';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/auth/Login';

const NAV_BG = '#2e3649';
const BRAND_ORANGE = '#ff7c38';

const navItems = [
  { text: 'Dashboard', icon: <DashboardIcon fontSize="small" />, path: '/dashboard' },
  { text: 'Projects', icon: <ProjectsIcon fontSize="small" />, path: '/projects' },
  { text: 'Extract', icon: <ExtractIcon fontSize="small" />, path: '/extract' },
  { text: 'Settings', icon: <SettingsIcon fontSize="small" />, path: '/settings' },
];

function AppContent() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Login />;
  }

  const isActive = (path) =>
    location.pathname === path || (path === '/dashboard' && location.pathname === '/');

  const handleNav = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ backgroundColor: NAV_BG, zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          {/* Left: logo + title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              component="img"
              src="/syllaworks-logo.png"
              alt="Syllaworks"
              sx={{ height: 36, width: 36, objectFit: 'contain' }}
            />
            <Box>
              <Typography variant="h6" noWrap component="div" sx={{ lineHeight: 1.1, fontWeight: 700, fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '0.01em' }}>
                now &gt; next &gt; not yet
              </Typography>
              <Typography variant="caption" noWrap sx={{ opacity: 0.7, lineHeight: 1 }}>
                by Syllaworks
              </Typography>
            </Box>
          </Box>

          {/* Center: desktop nav */}
          <Box sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            display: { xs: 'none', md: 'flex' },
            gap: 0.5,
          }}>
            {navItems.map((item) => (
              <Button
                key={item.text}
                startIcon={item.icon}
                onClick={() => handleNav(item.path)}
                sx={{
                  color: isActive(item.path) ? BRAND_ORANGE : 'rgba(255,255,255,0.7)',
                  fontWeight: isActive(item.path) ? 700 : 400,
                  borderBottom: isActive(item.path) ? `2px solid ${BRAND_ORANGE}` : '2px solid transparent',
                  borderRadius: 0,
                  px: 2,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  '&:hover': {
                    color: '#fff',
                    backgroundColor: 'rgba(255,255,255,0.07)',
                  },
                }}
              >
                {item.text}
              </Button>
            ))}
          </Box>

          {/* Right: user info + logout (desktop) and hamburger (mobile) */}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="caption"
              sx={{ opacity: 0.7, display: { xs: 'none', md: 'block' }, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {user?.name}
            </Typography>
            <Tooltip title="Sign out">
              <IconButton
                color="inherit"
                onClick={() => logout()}
                sx={{ display: { xs: 'none', md: 'flex' } }}
                aria-label="sign out"
              >
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton
              color="inherit"
              edge="end"
              onClick={() => setDrawerOpen(true)}
              aria-label="open menu"
              sx={{ display: { xs: 'flex', md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 220, backgroundColor: NAV_BG, color: 'rgba(255,255,255,0.85)' } }}
      >
        <Toolbar />
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
        <List sx={{ pt: 1 }}>
          {navItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={isActive(item.path)}
                onClick={() => handleNav(item.path)}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  color: isActive(item.path) ? BRAND_ORANGE : 'rgba(255,255,255,0.7)',
                  '&.Mui-selected': {
                    backgroundColor: `${BRAND_ORANGE}22`,
                    '&:hover': { backgroundColor: `${BRAND_ORANGE}33` },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.07)',
                    color: '#fff',
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: isActive(item.path) ? 600 : 400 }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mt: 1 }} />
        <List sx={{ pb: 1 }}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => { logout(); setDrawerOpen(false); }}
              sx={{ mx: 1, borderRadius: 1, color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.07)' } }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Sign Out" primaryTypographyProps={{ fontSize: '0.9rem' }} />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3, mt: '64px' }}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/extract" element={<Extract />} />
          <Route path="/settings" element={<Settings />} />
        </Routes>
      </Box>
    </Box>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
